import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, LogOut, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PLANS = [
  {
    lookup: "roloai_monthly",
    name: "Monthly",
    price: "$0.99",
    per: "/month",
    note: "Billed monthly. Cancel anytime.",
    highlight: false,
  },
  {
    lookup: "roloai_yearly",
    name: "Yearly",
    price: "$9.99",
    per: "/year",
    save: "Save $1.89/yr — 2 months free (16% off)",
    note: "Best value — 2 months free vs monthly.",
    highlight: true,
  },
];

const FEATURES = [
  "AI scan — front & back, auto-filled",
  "Unlimited contacts in your Rolodex",
  "Search by name, company & industry",
  "Share via QR, vCard, SMS & email",
  "Follow-up reminders with email nudges",
];

export default function Paywall() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState("");

  const choose = async (lookup) => {
    setLoading(lookup);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] dotted-bg">
      <header className="glass sticky top-0 z-40 px-4 sm:px-6 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0A2540] to-[#2EA6FF] flex items-center justify-center">
              <span className="font-heading text-white font-extrabold text-lg leading-none">D</span>
            </div>
            <div>
              <span className="font-heading text-lg font-extrabold tracking-tight leading-none block">Dextyou</span>
              <span className="text-[10px] text-[#2EA6FF] font-mono tracking-wider">LET ME DEXTYOU</span>
            </div>
          </div>
          <button
            data-testid="paywall-logout-button"
            onClick={logout}
            className="p-2 rounded-lg border border-slate-800 text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
            aria-label="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <img src="/dextyou-logo.png" alt="Dextyou" className="w-44 mx-auto rounded-2xl bg-white p-3 mb-6 shadow-xl" />
          <p className="overline text-[#2EA6FF] mb-2">Choose your plan</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">
            Turn every card into a contact
          </h1>
          <p className="text-slate-400 text-base leading-relaxed">
            Welcome{user?.name ? `, ${user.name}` : ""}! Pick a plan to unlock scanning, your full
            Rolodex, sharing and follow-up nudges.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.lookup}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              data-testid={`plan-card-${plan.lookup}`}
              className={`relative rounded-2xl p-7 border ${
                plan.highlight
                  ? "border-blue-500 bg-gradient-to-b from-blue-500/10 to-[#111827] shadow-[0_20px_50px_rgba(217,119,6,0.15)]"
                  : "border-slate-800 bg-[#111827]"
              }`}
            >
              {plan.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-600 text-white px-3 py-1 rounded-full">
                  <Sparkles className="w-3 h-3" /> Best value
                </span>
              )}
              <h3 className="font-heading text-lg font-semibold tracking-tight text-slate-300">{plan.name}</h3>
              <div className="flex items-end gap-1 mt-2 mb-1">
                <span className="font-heading text-4xl font-extrabold tracking-tight text-white">{plan.price}</span>
                <span className="text-slate-400 mb-1 text-sm">{plan.per}</span>
              </div>
              {plan.save && (
                <span
                  data-testid="yearly-savings-badge"
                  className="inline-block text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1 mb-2"
                >
                  {plan.save}
                </span>
              )}
              <p className="text-xs text-slate-500 mb-6">{plan.note}</p>

              <ul className="space-y-2.5 mb-7">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>

              <button
                data-testid={`subscribe-button-${plan.lookup}`}
                onClick={() => choose(plan.lookup)}
                disabled={!!loading}
                className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${
                  plan.highlight
                    ? "bg-blue-600 hover:bg-blue-500 text-white"
                    : "border border-slate-700 text-white hover:border-blue-500"
                }`}
              >
                {loading === plan.lookup ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  `Get ${plan.name}`
                )}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-slate-600 mt-8">
          Secure checkout by Stripe · Cancel anytime.
        </p>
      </main>
    </div>
  );
}
